import React, { useState } from 'react';
import { Link } from 'react-router';

import {
  Box,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  EmptyState,
  LoadingOverlay,
  LoadingSpinner,
  LoadingText,
  StructuredList,
  StructuredListHeader,
  StructuredListItem,
} from '@saas-ui/react';
import {
  Bell,
  BellDot,
  LucideChevronRight,
  LucideMailOpen,
} from 'lucide-react';

import { InfiniteScroll, LoadingScreen } from '@/components/common';
import { Button, IconButton } from '@/components/core';
import {
  FONTS_OPACITY,
  NOTIFICATIONS_SCROLLABLE_TARGET_ID,
  ROUTES,
} from '@/constant';
import { cn } from '@/lib/utils';
import {
  useBulkUpdateNotifications,
  useGetNotifications,
  useMarkAllNotificationsAsRead,
  useNotificationsChannel,
} from '@/store/notifications';
import {
  NotificationsStatusFilter,
  Notification as NotificationType,
} from '@/types/notifications';

import { NotificationItem } from './NotificationItem';

export const NotificationMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    totalCount,
    notifications,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetNotifications({ status: NotificationsStatusFilter.UNREAD });

  const { mutate: updateNotifications } = useBulkUpdateNotifications();
  const markAllAsRead = useMarkAllNotificationsAsRead();

  useNotificationsChannel();

  const fetchMore = () => {
    fetchNextPage();
  };

  const markAsRead = (notification: NotificationType) => {
    updateNotifications({
      notificationIds: [notification.id],
      status: 'read',
    });

    // React infinite scroll breaks if scrollable target is no longer scrollable
    // This may happen if user marks all notifications as read
    // So we need to check if scrollable target is still scrollable and manually trigger next fetch
    const element = document.getElementById(NOTIFICATIONS_SCROLLABLE_TARGET_ID);
    if (element?.scrollHeight === element?.clientHeight) {
      fetchMore();
    }
  };

  const handleClick = (notification: NotificationType) => {
    markAsRead(notification);

    if (notification.targetURL) {
      window.open(notification.targetURL, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Menu
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      isLazy
      placement='bottom-end'
    >
      <MenuButton
        as={IconButton}
        variant='glass-item'
        position='relative'
        size='sm'
        aria-label='Notifications'
      >
        <Icon
          as={totalCount && totalCount > 0 ? BellDot : Bell}
          boxSize={3.5}
          color='white'
          className={cn({
            '[&>circle]:text-red-500': totalCount && totalCount > 0,
            '[&>circle]:fill-red-500': totalCount && totalCount > 0,
          })}
        />
      </MenuButton>

      <Portal>
        <MenuList
          display='flex'
          flexDirection='column'
          w='min(432px, calc(100vw - 2rem))'
          maxH={{ base: 'calc(100dvh - 3.25rem)', lg: '85vh' }}
          mx='auto'
          p={0}
          bg='cards.bg'
          borderRadius='lg'
          border='1px solid!'
          borderColor='border.light!'
          boxShadow='none!'
          overflow='hidden'
          zIndex='modal'
        >
          {isLoading ? (
            <Box p={4}>
              <LoadingScreen
                variant='fill'
                size='md'
                text='Loading notifications...'
              />
            </Box>
          ) : (
            <>
              <StructuredList
                p='0'
                minH={0}
                display='flex'
                flexDirection='column'
                overflow='hidden'
              >
                <StructuredListHeader
                  color='fonts.primary'
                  bg='cards.bg'
                  position='sticky'
                  top='0'
                  zIndex='popover'
                  p={4}
                >
                  <HStack justify='space-between' gap={3}>
                    <Text size='primarySemiBold'>Notifications</Text>
                    {totalCount && totalCount > 0 && (
                      <Button
                        size='sm'
                        variant='link'
                        colorScheme='white'
                        fontWeight='semibold'
                        _hover={{ opacity: FONTS_OPACITY }}
                        onClick={() => markAllAsRead.mutate()}
                        isLoading={markAllAsRead.isPending}
                        leftIcon={<LucideMailOpen size={13} />}
                      >
                        <Text size='secondaryRegular'>Mark all as read</Text>
                      </Button>
                    )}
                  </HStack>
                </StructuredListHeader>

                {notifications?.length === 0 ? (
                  <StructuredListItem maxH='full'>
                    <EmptyState
                      description="You're all caught up!"
                      p={8}
                      borderRadius='md'
                      bg='app-background'
                    />
                  </StructuredListItem>
                ) : (
                  <Box
                    id={NOTIFICATIONS_SCROLLABLE_TARGET_ID}
                    overflowY='auto'
                    borderTop='1px solid'
                    borderBottom='1px solid'
                    borderColor='border.light'
                  >
                    <InfiniteScroll
                      dataLength={notifications?.length ?? 0}
                      next={fetchMore}
                      hasMore={hasNextPage}
                      isFetching={isFetchingNextPage}
                      scrollableTarget={NOTIFICATIONS_SCROLLABLE_TARGET_ID}
                      loader={
                        <LoadingOverlay variant='fill'>
                          <HStack alignItems='center' justifyContent='center'>
                            <LoadingSpinner size='sm' />
                            <LoadingText fontSize='sm'>Loading...</LoadingText>
                          </HStack>
                        </LoadingOverlay>
                      }
                      showEndMessage
                      endMessage={
                        <Text
                          fontSize='xs'
                          color='fonts.secondary'
                          textAlign='center'
                          py={3}
                        >
                          No more notifications
                        </Text>
                      }
                    >
                      <Stack gap={0}>
                        {notifications?.map(notification => (
                          <NotificationItem
                            key={notification.id}
                            notification={notification}
                            onClick={handleClick}
                            onMarkAsRead={markAsRead}
                          />
                        ))}
                      </Stack>
                    </InfiniteScroll>
                  </Box>
                )}
              </StructuredList>
              <HStack alignItems='center' justifyContent='center' p={4}>
                <Button
                  color='alternative.purple'
                  variant='link'
                  rightIcon={<LucideChevronRight size={13} />}
                  fontWeight='normal'
                  as={Link}
                  onClick={() => setIsOpen(false)}
                  {...{ to: ROUTES.NOTIFICATIONS }}
                >
                  Open all notifications
                </Button>
              </HStack>
            </>
          )}
        </MenuList>
      </Portal>
    </Menu>
  );
};
